// src/components/instructor/ops/opsShared.js
//
// Pass 49B §23 — shared visual vocabulary for the operations views, with ACTIVITY and PAYMENT
// kept as SEPARATE legends. Activity describes what the work is; payment applies ONLY to
// client bookings. A driving event never shows a payment indicator.

import React from 'react';
import { ACTIVITY, ACTIVITY_LABEL, deriveActivityType } from '../../../utils/activityModel';
import { normalizePaymentState, PAYMENT, paymentLabel } from '../../../utils/paymentSemantics';
import { isClientBooking } from '../../../utils/scheduleModel';
import { zonedTimeLabel, LEGACY_OPS_TIMEZONE } from '../../../utils/opsTime';

// Distinct colour per activity (NOT payment).
export const ACTIVITY_COLOR = {
  [ACTIVITY.LESSON]: { bg: '#EFF4FE', fg: '#1A3FA0' },
  [ACTIVITY.DRIVING]: { bg: '#FEF3C7', fg: '#92400E' },
  [ACTIVITY.CONCIERGE]: { bg: '#F5F3FF', fg: '#4C1D95' },
  [ACTIVITY.ADMIN]: { bg: '#ECEAE6', fg: '#57534E' },
  [ACTIVITY.TRAINING]: { bg: '#E1F5EE', fg: '#085041' },
  [ACTIVITY.MEETING]: { bg: '#FDE8E8', fg: '#991B1B' },
  [ACTIVITY.TIMEOFF]: { bg: '#F4F3F0', fg: '#9E9B95' },
};
export const activityColor = (a) => ACTIVITY_COLOR[a] || { bg: '#F4F3F0', fg: '#57534E' };

// Payment badge colour — SEPARATE axis, client bookings only.
export const PAYMENT_COLOR = {
  [PAYMENT.PAID]: { bg: '#E1F5EE', fg: '#085041', label: 'Paid' },
  [PAYMENT.DEPOSIT]: { bg: '#EFF4FE', fg: '#1A3FA0', label: 'Deposit' },
  [PAYMENT.NOT_PAID]: { bg: '#FEF2F2', fg: '#991B1B', label: 'Not paid' },
  [PAYMENT.INQUIRY]: { bg: '#FEF3C7', fg: '#92400E', label: 'Inquiry' },
  [PAYMENT.NOT_RECORDED]: { bg: '#F4F3F0', fg: '#9E9B95', label: 'Not recorded' },
};

const chip = (bg, fg) => ({ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 10.5, fontWeight: 600, background: bg, color: fg });

export function ActivityBadge({ booking }) {
  const a = deriveActivityType(booking);
  const c = activityColor(a);
  return <span style={chip(c.bg, c.fg)}>{ACTIVITY_LABEL[a] || a}</span>;
}

// §23 payment indicator ONLY for client bookings; nothing for driving/concierge/admin events.
export function PaymentBadge({ booking }) {
  if (!isClientBooking(booking)) return null;
  const state = normalizePaymentState(booking.paymentState ?? booking.paymentStatus);
  const c = PAYMENT_COLOR[state] || PAYMENT_COLOR[PAYMENT.NOT_RECORDED];
  return <span style={chip(c.bg, c.fg)} title={paymentLabel[state]} data-testid="payment-badge">{c.label}</span>;
}

export function ActivityPaymentLegend() {
  const swatch = (bg, fg, text) => (
    <span key={text} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 10 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: bg, border: `1px solid ${fg}33` }} />
      <span style={{ fontSize: 11, color: '#6B6860' }}>{text}</span>
    </span>
  );
  return (
    <div data-testid="activity-payment-legend" style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', padding: '8px 0' }}>
      <div>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#9E9B95', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 8 }}>Activity</span>
        {[ACTIVITY.LESSON, ACTIVITY.DRIVING, ACTIVITY.CONCIERGE, ACTIVITY.ADMIN, ACTIVITY.TRAINING, ACTIVITY.MEETING, ACTIVITY.TIMEOFF]
          .map((a) => swatch(activityColor(a).bg, activityColor(a).fg, ACTIVITY_LABEL[a]))}
      </div>
      <div>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#9E9B95', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 8 }}>Payment (client bookings)</span>
        {[PAYMENT.PAID, PAYMENT.DEPOSIT, PAYMENT.NOT_PAID, PAYMENT.INQUIRY, PAYMENT.NOT_RECORDED]
          .map((p) => swatch(PAYMENT_COLOR[p].bg, PAYMENT_COLOR[p].fg, PAYMENT_COLOR[p].label))}
      </div>
    </div>
  );
}

// Pass 82C.2 §19 — display the operational wall-clock in the ORGANIZATION's timezone, regardless of the browser
// timezone. Defaults to the legacy SAS zone (Asia/Tokyo) so every existing single-arg caller stays byte-identical;
// org-aware callers (Day/Week/Season/Calendar) pass the selected org's zone so a non-JST tenant shows local time.
export function fmtTime(v, timeZone = LEGACY_OPS_TIMEZONE) {
  return zonedTimeLabel(v, timeZone);
}
